import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import { memo, useState } from 'react';
import copyText from '../../core/util/copyText';
import { baseUrl } from '../../main/client';
import importCodeTemplate from '../automation/importCodeTemplate';

interface Guest {
    id: number;
    firstName: string;
    lastName: string;
    sex: string;
    nationality: string;
    country: string;
    birthDate: string;
    birthPlace: string;
    postcode: string;
    address: string;
    idDocumentType?: string;
    idDocumentId?: string;
    idDocumentIsPdf: boolean;
    valid: boolean;
    price: number;
    touristTax: number;
    nationalityNameHU: string;
    countryNameHU: string;
}

const GuestDocumentView = memo(
    ({
        open,
        setOpened,
        setClosed,
        showNext,
        showPrevious,
        bookingId,
        guest,
        guestIndex,
    }: {
        open: boolean;
        setOpened: () => void;
        setClosed: () => void;
        showNext: () => void;
        showPrevious: () => void;
        bookingId: number;
        guest: Guest;
        guestIndex: number;
    }) => {
        const [copiedAutomationCode, setCopiedAutomationCode] = useState(false);
        const [copiedPostcode, setCopiedPostcode] = useState(false);
        const [copiedBirthPlace, setCopiedBirthPlace] = useState(false);
        const [rotation, setRotation] = useState('0deg');

        const documentUrl = `${baseUrl}/api/guest/document/${bookingId}/${guestIndex}?key=${localStorage.getItem('token')}`;
        const isPdf = guest.idDocumentIsPdf;

        const importCode = `
        ${importCodeTemplate}(${JSON.stringify(guest)}).catch(error => {
            throw error;
        });
    `;

        return (
            <>
                {isPdf ? (
                    <Box
                        role="button"
                        tabIndex={0}
                        onClick={setOpened}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setOpened();
                            }
                        }}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f5f5f5',
                            border: '2px dashed #ccc',
                            cursor: 'pointer',
                            height: '100%',
                            '&:hover': {
                                backgroundColor: '#e0e0e0',
                            },
                        }}
                    >
                        <Typography variant="body2" color="textSecondary">
                            PDF Document - Click to view
                        </Typography>
                    </Box>
                ) : (
                    <div // NOSONAR
                        role="button"
                        tabIndex={0}
                        onClick={setOpened}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setOpened();
                            }
                        }}
                        style={{
                            backgroundImage: `url(${documentUrl})`,
                            backgroundSize: 'contain',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'center center',
                            cursor: 'pointer',
                        }}
                    />
                )}
                {open && (
                    <Dialog open={open} onClose={setClosed} fullScreen>
                        <DialogContent>
                            {isPdf ? (
                                <embed
                                    src={documentUrl}
                                    type="application/pdf"
                                    style={{
                                        width: '100%',
                                        height: '90vh',
                                    }}
                                />
                            ) : (
                                <div
                                    style={{
                                        transform: `rotate(${rotation})`,
                                        height: '90vh',
                                        width: '90vw',
                                        backgroundImage: `url(${documentUrl})`,
                                        backgroundSize: 'contain',
                                        backgroundRepeat: 'no-repeat',
                                        backgroundPosition: 'center center',
                                    }}
                                />
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Typography>
                                <Button
                                    onClick={() => {
                                        copyText(guest.birthPlace)
                                            .then(() => {
                                                setCopiedBirthPlace(true);
                                            })
                                            .catch(error => {
                                                throw error;
                                            });
                                    }}
                                >
                                    Birth place: {guest.birthPlace}
                                    {copiedBirthPlace ? ' Copied' : ''}
                                </Button>
                            </Typography>
                            <Typography>
                                <Button
                                    onClick={() => {
                                        copyText(guest.postcode)
                                            .then(() => {
                                                setCopiedPostcode(true);
                                            })
                                            .catch(error => {
                                                throw error;
                                            });
                                    }}
                                >
                                    Postcode: {guest.postcode}
                                    {copiedPostcode ? ' Copied' : ''}
                                </Button>
                            </Typography>
                            <Button
                                onClick={() => {
                                    copyText(importCode)
                                        .then(() => {
                                            setCopiedAutomationCode(true);
                                        })
                                        .catch(error => {
                                            throw error;
                                        });
                                }}
                            >
                                {copiedAutomationCode
                                    ? 'Copied'
                                    : 'Copy details automation code'}
                            </Button>
                            <Button
                                onClick={() => {
                                    window.open(documentUrl);
                                }}
                            >
                                Open document
                            </Button>{' '}
                            {!isPdf && (
                                <>
                                    <Button
                                        onClick={() => {
                                            setRotation('90deg');
                                        }}
                                    >
                                        Rotate 90
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            setRotation('270deg');
                                        }}
                                    >
                                        Rotate 270
                                    </Button>
                                </>
                            )}
                            <Button onClick={showPrevious}>Previous</Button>
                            <Button onClick={showNext}>Next</Button>
                        </DialogActions>
                    </Dialog>
                )}
            </>
        );
    }
);

export default GuestDocumentView;
